import * as React from 'react';
import clsx from 'clsx';
import Icons from '@/const/icons';
import { useRouter } from 'next/router';
import { CardContainer } from '@/components/dashboard';
import SimpleTable from '@/components/common/tables/simpleTable';
import { Avatar, LoaderScreen, Typography } from '@/components/common';
import { TeamMember } from '@/interfaces';
import { RoleFilter } from './RoleFilter';
import { ActionColumn } from './ActionColumn';
import { AdminPanelHeader } from './AdminPanelHeader';
import { Combobox } from '@/components/ui/combobox';
import { ChevronDown } from 'lucide-react';
import { EmptyStateFilter } from '../common/EmptyStateFilter';
import { useQuery } from '@tanstack/react-query';
import { GET_LAWYER_ROLES, GET_TEAM_MEMBERS, userLawyerService } from '@/api';
import { format, parseISO } from 'date-fns';
import { Options } from '@/components/ui/combobox';
import { ModalLawyerDetail } from './modal-lawyer-detail/ModalLawyerDetail';
import { useNotification } from '@/hooks';
import { Allowed } from '@/components/common/allowed/Allowed';
import { usePermissions } from '@/context/PermissionsContext';

const parseRoleNumbers = (role: unknown): number[] | undefined => {
	if (typeof role !== 'string') return undefined;

	return role
		.split(',')
		.map((item) => Number(item.trim()))
		.filter((num) => !isNaN(num));
};

const AdminPanel = (): React.JSX.Element => {
	const LIMIT = 10;
	const router = useRouter();
	const pathname = router.pathname;
	const { page, search, role } = router.query;
	const { allowed } = usePermissions();
	const [idLawyer, setIdLawyer] = React.useState<string | undefined>();

	const {
		data: teamMemberList,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [GET_TEAM_MEMBERS, search, page, role],
		queryFn: () =>
			userLawyerService.getTeamMembers(
				Number(page || 1),
				LIMIT,
				search as string,
				parseRoleNumbers(role)
			),
	});

	const { data: roles, isLoading: isLoadingRoles } = useQuery({
		queryKey: [GET_LAWYER_ROLES],
		queryFn: () => userLawyerService.getLawyerRolesList(),
	});

	const roleOptions: Options[] = React.useMemo(() => {
		return (
			roles?.data
				?.map((item) => ({
					label: item.name ?? '',
					value: String(item.id),
				}))
				.filter((option) => option.label !== '' && option.value != null) ?? []
		);
	}, [roles]);

	const onClickRow = (data: TeamMember): void => {
		if (data.deleted_at) return;
		setIdLawyer(data.id);

		router.replace(
			{
				pathname,
				query: {
					...(page && { page }),
					...(search && { search }),
					...(role && { role }),
				},
			},
			undefined,
			{
				shallow: true,
			}
		);
	};
	return (
		<>
			<CardContainer
				title="Team Members"
				classNameContent="!h-fit overflow-visible"
				icon={Icons.profileUsersEmptyState}
				customButton={
					<AdminPanelHeader roleOptions={roleOptions} refetch={refetch} />
				}
			>
				{isLoading || isLoadingRoles ? (
					<LoaderScreen className="my-20" />
				) : (
					<SimpleTable<TeamMember & { isActive: boolean }>
						classNameContainer={clsx(
							'border-none shadow-none lg:!overflow-x-visible',
							{
								'h-full': teamMemberList?.data.total === 0,
							}
						)}
						classNameHead="bg-white !opacity-100"
						classNameHeader={clsx(
							'w-full min-w-[900px] lg:min-w-full grid grid-cols-[2fr_2fr_repeat(3,1fr)]',
							{
								'!grid-cols-[2fr_2fr_1fr_1fr]': !allowed.has('delete_users'),
							}
						)}
						classNameRows={clsx(
							'grid grid-cols-[2fr_2fr_repeat(3,1fr)] cursor-pointer hover:!bg-green-secondary hover:border-solid items-center',
							{
								'!grid-cols-[2fr_2fr_1fr_1fr]': !allowed.has('delete_users'),
							}
						)}
						showLastRowBorderBottom
						hideRowActions
						hideFirstRowBorder
						totalPages={teamMemberList?.data.totalPages}
						tableEmptyComponent={
							<div className="lg:mt-20 lg:mb-40">
								<EmptyStateFilter
									icon={Icons.filterEmptyStateBgGray}
									iconClassName="w-[175px] h-[140px]"
									message={'No results found.'}
									subMessage={
										'Please try another search or adjust your filtering.'
									}
								/>
							</div>
						}
						onClickRow={onClickRow}
						columns={[
							{
								header: (
									<Typography type="regular-12" className="text-opacity-black">
										Lawyer Name
									</Typography>
								),
								content: (instance) => (
									<div className="flex items-center gap-x-2 min-w-0">
										<Avatar
											photoUrl={
												instance.profile_picture
													? instance.profile_picture
													: !instance.firstname
													? Icons.avatar
													: ''
											}
											firstname={instance.firstname}
											lastname={instance.lastname}
											size="extra-small"
										/>
										<Typography
											type="regular-14"
											className={clsx('truncate flex-1 min-w-0', {
												'!text-gray-0': !!instance.deleted_at,
											})}
										>
											{instance.firstname || instance.lastname
												? ` ${instance.firstname} ${instance.lastname}`
												: ''}
										</Typography>
									</div>
								),
							},
							{
								header: (
									<Typography type="regular-12" className="text-opacity-black">
										Email
									</Typography>
								),
								content: (instance) => (
									<Typography
										type="regular-14"
										className={clsx({
											'!text-gray-0': !!instance.deleted_at,
										})}
									>
										{instance.email}
									</Typography>
								),
							},
							{
								header: (
									<Typography type="regular-12" className="text-opacity-black">
										Last active
									</Typography>
								),
								content: (instance) => (
									<Typography
										type="regular-14"
										className={clsx({
											'text-main-orange': !instance.last_active,
											'!text-gray-0': !!instance.deleted_at,
										})}
									>
										{instance.last_active
											? format(parseISO(instance.last_active), 'MM-dd-yyyy')
											: 'Invitation sent'}
									</Typography>
								),
							},
							{
								header: <RoleFilter roleOptions={roleOptions} />,
								content: (instance) => (
									<RoleColumn
										user={instance}
										roleOptions={roleOptions}
										refetch={refetch}
									/>
								),
							},
							...(allowed.has('delete_users')
								? [
										{
											header: '',
											content: (instance: any) => (
												<Allowed need={'delete_users'}>
													<ActionColumn user={instance} refetch={refetch} />
												</Allowed>
											),
										},
								  ]
								: []),
						]}
						rows={teamMemberList?.data.items?.map((item) => ({
							...item,
							isActive: false,
						}))}
					/>
				)}
			</CardContainer>
			{idLawyer && <ModalLawyerDetail id={idLawyer} setId={setIdLawyer} />}
		</>
	);
};

interface RoleColumnProps {
	user: TeamMember;
	roleOptions: Options[];
	refetch: () => void;
}

const RoleColumn: React.FC<RoleColumnProps> = ({
	roleOptions,
	user,
	refetch,
}) => {
	const [openRole, setOpenRole] = React.useState(false);
	const { toastError, toastSuccess } = useNotification();

	const defaultValue: Options | undefined = React.useMemo(() => {
		return roleOptions.find((item) => item.label === user.role);
	}, [user.role, roleOptions]);

	const handleChangeRole = async (value: string | undefined): Promise<void> => {
		try {
			await userLawyerService.updateRoleLawyer(user.id, Number(value));
			refetch();
			toastSuccess('Role changed successfully');
		} catch (error) {
			toastError('An error has occurred. Please try again later.');
		}
	};

	return (
		<div onClick={(e) => e.stopPropagation()}>
			{defaultValue && (
				<Allowed
					need={'manage_roles'}
					otherwise={
						<Typography type="regular-14" className="text-primary">
							{user.role}
						</Typography>
					}
				>
					<Combobox
						value={user.role}
						open={openRole}
						defaultSelected={defaultValue}
						disabled={!!user.deleted_at}
						buttonContent={
							<div className="flex items-center gap-x-2 relative w-fit">
								<Typography
									type="regular-14"
									className={clsx(
										user.deleted_at ? 'text-gray-0' : 'text-primary'
									)}
								>
									{user.role}
								</Typography>
								<ChevronDown
									className={`mt-0.5 h-5 w-5 shrink-0 ${
										user.deleted_at ? 'text-gray-0' : 'text-primary'
									} ${openRole && 'rotate-180'}`}
								/>
							</div>
						}
						options={roleOptions}
						placeholder=""
						classNameTrigger="!border-none !w-fit !p-0"
						classNamePopoverContent="!w-[176px] !border-solid !border-gray-disabled shadow-shadow2"
						setOpen={(value) => setOpenRole(value)}
						setValue={(value) => handleChangeRole(value)}
						align="end"
					/>
				</Allowed>
			)}
		</div>
	);
};

export default AdminPanel;
